import React from 'react';
import { Link } from 'react-router-dom';
import { HelpGuidesLayout } from './HelpGuidesLayout';
import { useHelpGuideDetail } from './useHelpGuideDetail';
import { formatGuideDate, toEmbedUrl } from './utils';

export const HelpGuideDetail: React.FC = () => {
  const { authLoading, isAuthenticated, isLoading, guide, categories, error, selectCategory } =
    useHelpGuideDetail();

  if (authLoading || isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner" />
          <p className="text-gray-600 font-medium">Loading guide...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const selectedCategoryId = guide?.categoryId || 'all';
  const embedUrl = guide?.videoUrl ? toEmbedUrl(guide.videoUrl) : null;

  return (
    <HelpGuidesLayout
      categories={categories}
      selectedCategoryId={selectedCategoryId}
      onSelectCategory={selectCategory}
      error={!guide ? error : null}
    >
      {!guide ? (
        <div className="text-center py-16">
          <p className="text-gray-700 mb-4">{error || 'Guide not found'}</p>
          <Link to="/help" className="btn-primary inline-flex">
            Back to all guides
          </Link>
        </div>
      ) : (
        <article>
          <div className="mb-4">
            <Link to="/help" className="text-sm text-primary-600 hover:text-primary-700">
              ← All guides
            </Link>
          </div>

          <div className="text-sm text-gray-500 mb-3">
            {guide.categoryName && (
              <>
                <span>{guide.categoryName}</span>
                <span className="mx-2">·</span>
              </>
            )}
            <span>Updated {formatGuideDate(guide.updatedAt)}</span>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-8">{guide.title}</h1>

          <div
            className="prose prose-gray max-w-none bg-white border border-gray-200 rounded-xl p-6 sm:p-8"
            dangerouslySetInnerHTML={{ __html: guide.contentHtml || '' }}
          />

          {embedUrl && (
            <div className="mt-8 aspect-video rounded-xl overflow-hidden border border-gray-200 bg-black">
              <iframe
                src={embedUrl}
                title={guide.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          {guide.galleryJson?.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Gallery</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {guide.galleryJson.map((item, index) => (
                  <img
                    key={`${item.url}-${index}`}
                    src={item.cloudfrontUrl || item.url}
                    alt={item.alt || `${guide.title} image ${index + 1}`}
                    className="w-full rounded-lg border border-gray-200 object-cover"
                  />
                ))}
              </div>
            </div>
          )}
        </article>
      )}
    </HelpGuidesLayout>
  );
};
